import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { StayCompliantNavItem } from "../../../../../src/js/common/service/server/Types";
import {
  ArrowPathRoundedSquareIcon,
  BookOpenIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  FolderIcon,
  HomeIcon,
  IdentificationIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  MapIcon,
  ShieldCheckIcon,
  SignalIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { NavItem } from "../../../../../src/js/polaris/components/layout/nav/config";
import _ from "lodash";
import { UserNavItem } from "../../../../../src/js/polaris/components/layout/nav/UserNavItem";
import { OrganizationDto } from "../../../../../src/js/common/service/server/dto/OrganizationDto";

export function BaseNavRedesign({
  navContent,
  settingsRoute,
  userProfileRoute,
  bottomInset = 0,
  logoRoute,
  logoAriaLabel,
}: {
  navContent: React.ReactNode;
  settingsRoute: string;
  userProfileRoute: string;
  bottomInset?: number;
  logoRoute: string;
  logoAriaLabel: string;
}) {
  return (
    // Need to use inline styles instead of tailwind since we are inter-mixing previous UI components which have styling conflicts with tailwind
    <div
      style={{
        width: "256px",
        paddingBottom: `${26 + bottomInset}px`,
        boxSizing: "border-box",
        background: "#ffffff",
        borderRight: "1px solid #E8E5EE",
        display: "flex",
        height: "100%",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          paddingTop: "64px",
          display: "flex",
          justifyContent: "flex-start",
          // Center the logo's circular mark over the nav item icon column:
          // icon centers sit at 36px (16px content + 10px item padding + 10px
          // half-icon); the mark's center is ~17px into the 150px-wide image.
          paddingLeft: "19px",
        }}
      >
        <Link to={logoRoute} aria-label={logoAriaLabel}>
          <img
            src="/assets/images/truevault-logo-print.png"
            width="150px"
            height="33px"
            alt="TrueVault Logo"
          />
        </Link>
      </div>

      <div
        style={{
          paddingBottom: "8px",
          paddingLeft: "16px",
          paddingRight: "16px",
          flexGrow: 1,
          marginTop: "20px",
          overflowY: "auto",
        }}
      >
        {navContent}
      </div>

      <div style={{ paddingLeft: "16px", paddingRight: "16px" }}>
        <div className="mb-[26px] h-px bg-neutral-200" />
        <UserNavItem
          useNewDesign
          settingsRoute={settingsRoute}
          userProfileRoute={userProfileRoute}
        />
      </div>
    </div>
  );
}

/**
 * Uppercase section heading used to group nav items (e.g. "Compliance", "Resources").
 */
export function NavSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="select-none px-[10px] pb-[6px] pt-[20px] text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">
      {children}
    </div>
  );
}

/**
 * Navigation items can be hidden either by setting `hidden` to `true` or by providing a function that evaluates the org
 * for whether a particular item should be hidden.
 *
 * @param navItem The navigation item for which to evaluate visibility.
 * @param org Organization configuration to inform visibility for the navigation item.
 */
const isNavItemHiddenForOrg = (navItem: NavItem, org: OrganizationDto): boolean => {
  return typeof navItem.hidden === "function"
    ? navItem.hidden(org)
    : typeof navItem.hidden == "undefined"
    ? false
    : navItem.hidden;
};

const NAV_ITEMS_TO_ICONS: Partial<Record<StayCompliantNavItem, React.ReactElement>> = {
  DASHBOARD: <HomeIcon />,
  REQUEST_INBOX: <InboxIcon />,
  RECORD_LOOKUP: <MagnifyingGlassIcon />,
  TASK_LIST: <ClipboardDocumentCheckIcon />,
  DATA_MAP_DASHBOARD: <MapIcon />,
  DOCUMENTS: <FolderIcon />,
  KNOWLEDGE_CENTER: <BookOpenIcon />,
  HR_CENTER: <IdentificationIcon />,
  PRIVACY_CENTER: <ShieldCheckIcon />,
  WORKFLOWS: <ArrowPathRoundedSquareIcon />,
  EVENT_PULSE: <SignalIcon />,
  RAMSAY: <SparklesIcon />,
};

type SidebarNavLinkRedesignProps = {
  navItem: NavItem;
  org: OrganizationDto;
  isDisabled?: boolean;
  notificationCount?: number;
  showExpandIcon?: boolean;
  onExpandClick?: (e: React.MouseEvent) => void;
  onRowClick?: () => void;
  refresh?: () => void;
};

export function SidebarNavLinkRedesign({
  isDisabled,
  navItem,
  org,
  notificationCount = 0,
  refresh,
}: SidebarNavLinkRedesignProps) {
  if (navItem.children && navItem.children?.length > 0) {
    return (
      <SidebarNavLinkWithChildrenRedesign
        isDisabled={isDisabled}
        navItem={navItem}
        org={org}
        notificationCount={notificationCount}
        refresh={refresh}
      />
    );
  } else {
    return (
      <SidebarNavLinkBaseRedesign
        isDisabled={isDisabled}
        navItem={navItem}
        org={org}
        notificationCount={notificationCount}
        refresh={refresh}
      />
    );
  }
}

function SidebarNavLinkBaseRedesign({
  isDisabled,
  navItem,
  notificationCount = 0,
  refresh,
  org,
  showExpandIcon,
  isExpanded, // Only used when showExpandIcon is true
  isChild,
  onExpandClick,
  onRowClick,
}: SidebarNavLinkRedesignProps & { isChild?: boolean; isExpanded?: boolean }) {
  const location = useLocation();

  let currentPath = location.pathname;
  if (currentPath.endsWith("/")) {
    currentPath = currentPath.slice(0, -1);
  }

  let routeStr = _.isFunction(navItem.route) ? navItem.route(org.id) : navItem.route;

  const isHidden = isNavItemHiddenForOrg(navItem, org);

  if (isHidden) {
    return null;
  }

  let isActive =
    [routeStr, ...(navItem.itemAliases || [])].filter((r) => currentPath == r).length > 0;
  if (navItem.matchRouteOnPrefix && routeStr) {
    isActive = isActive || currentPath.startsWith(routeStr);
  }
  const bgColor = isActive ? "bg-primary-100" : isDisabled ? "" : "hover:bg-neutral-100";
  const textColor = isActive
    ? "text-primary-600"
    : isDisabled
    ? "text-neutral-400"
    : "text-neutral-600";
  const hoverTextColor = isActive || isDisabled ? "" : "hover:text-neutral-900";
  const pressEffect = isDisabled ? "" : "active:scale-[0.98]";

  let icon = NAV_ITEMS_TO_ICONS[navItem.item];
  if (icon) {
    icon = React.cloneElement(icon, { className: "w-5 h-5" });
  }

  const labelNode = (
    <div className="flex items-center">
      {icon && <span className="mr-[12px] flex shrink-0">{icon}</span>}
      <span className="text-[14px] font-medium">{navItem.label}</span>
    </div>
  );

  let resultElem: React.ReactNode;
  if (showExpandIcon) {
    resultElem = (
      <div className="flex items-center justify-between">
        {labelNode}
        <div
          onClick={onExpandClick}
          className={`cursor-pointer rounded-[6px] p-[4px] transition-colors duration-150 hover:bg-neutral-200/60 ${
            isActive ? "text-primary-600" : "text-neutral-400 group-hover:text-neutral-600"
          }`}
          style={{
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 200ms ease-in-out",
          }}
        >
          <ChevronRightIcon className="h-4 w-4" strokeWidth={2} />
        </div>
      </div>
    );
  } else if (notificationCount > 0) {
    const badgeColors = isDisabled
      ? "bg-neutral-300 text-white"
      : isActive
      ? "bg-primary-600 text-white"
      : "bg-neutral-100 text-neutral-700 group-hover:bg-neutral-200 group-hover:text-neutral-900";
    resultElem = (
      <div className="flex items-center justify-between">
        {labelNode}
        <div
          className={`min-w-[20px] shrink-0 rounded-full px-[7px] text-center text-[11px] font-semibold leading-[18px] transition-colors duration-150 ${badgeColors}`}
        >
          {notificationCount}
        </div>
      </div>
    );
  } else {
    resultElem = labelNode;
  }

  let padding = isChild ? "pl-[12px]" : "pl-[10px]";
  const itemClasses = `group relative block py-[8px] pr-[10px] ${padding} ${bgColor} ${textColor} ${hoverTextColor} ${pressEffect} rounded-[8px] transition-all duration-150 ease-out`;
  return (
    <div className="mb-[2px]">
      {routeStr && !isDisabled ? (
        // Group headers navigate (so they can be selected) and expand/collapse
        // via onRowClick; the chevron toggles without navigating.
        <NavLink
          to={routeStr}
          onClick={() => {
            refresh?.();
            onRowClick?.();
          }}
          className={`${itemClasses}${showExpandIcon ? " select-none" : ""}`}
        >
          {resultElem}
        </NavLink>
      ) : showExpandIcon && !isDisabled ? (
        <div className={`${itemClasses} cursor-pointer select-none`} onClick={onExpandClick}>
          {resultElem}
        </div>
      ) : (
        <div className={itemClasses}>{resultElem}</div>
      )}
    </div>
  );
}

function SidebarNavLinkWithChildrenRedesign({
  isDisabled,
  navItem,
  notificationCount = 0,
  refresh,
  org,
}: SidebarNavLinkRedesignProps) {
  const location = useLocation();

  let currentPath = location.pathname;
  if (currentPath.endsWith("/")) {
    currentPath = currentPath.slice(0, -1);
  }

  let routeStr = _.isFunction(navItem.route) ? navItem.route(org.id) : navItem.route;
  const isHidden = isNavItemHiddenForOrg(navItem, org);
  const isActive =
    [routeStr, ...(navItem.itemAliases || [])].filter((r) => currentPath == r).length > 0;
  const isAnyChildActive = navItem.children?.some((childNavItem) => {
    let childRouteStr = _.isFunction(childNavItem.route)
      ? childNavItem.route(org.id)
      : childNavItem.route;
    let isChildActive =
      [childRouteStr, ...(childNavItem.itemAliases || [])].filter((r) => currentPath == r).length >
      0;
    if (childNavItem.matchRouteOnPrefix && childRouteStr) {
      isChildActive = isChildActive || currentPath.startsWith(childRouteStr);
    }
    return isChildActive;
  });

  const [isExpanded, setIsExpanded] = useState(isActive || isAnyChildActive);

  // Reveal the group whenever navigation lands on it or one of its children
  // (deep links, cross-group clicks); manual collapse is otherwise preserved.
  const shouldAutoExpand = isActive || isAnyChildActive;
  useEffect(() => {
    if (shouldAutoExpand) {
      setIsExpanded(true);
    }
  }, [shouldAutoExpand]);

  if (isHidden) {
    return null;
  }

  return (
    <div>
      <SidebarNavLinkBaseRedesign
        isDisabled={isDisabled}
        navItem={navItem}
        org={org}
        notificationCount={notificationCount}
        refresh={refresh}
        showExpandIcon={true}
        isExpanded={isExpanded}
        onExpandClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsExpanded((s) => !s);
        }}
        onRowClick={() => {
          // Navigating into the group always reveals it; clicking the row
          // while already on the group's page toggles it closed/open.
          if (!isExpanded) {
            setIsExpanded(true);
          } else if (isActive) {
            setIsExpanded(false);
          }
        }}
      />

      <div
        style={{
          display: "grid",
          gridTemplateRows: isExpanded ? "1fr" : "0fr",
          transition: "grid-template-rows 250ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            overflow: "hidden",
            opacity: isExpanded ? 1 : 0,
            transform: isExpanded ? "translateY(0)" : "translateY(-4px)",
            transition: "opacity 200ms ease, transform 250ms ease",
          }}
        >
          <div className="mb-[4px] ml-[19px] mt-[2px] border-l-2 border-neutral-200 pl-[8px]">
            {navItem.children?.map((childNavItem) => {
              return (
                <SidebarNavLinkBaseRedesign
                  key={childNavItem.item}
                  navItem={childNavItem}
                  org={org}
                  refresh={refresh}
                  isChild={true}
                  isDisabled={isDisabled || childNavItem.isDisabled === true}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
